import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { ArrowLeft, Plus, Handshake, Users, Building2, Loader2, Crown, Trash2, Pencil, Phone, Mail, DollarSign, TrendingUp, StickyNote, PhoneCall, CalendarClock, Check, X, Kanban, Upload, Download, LayoutDashboard, AlertTriangle, FileText, Package, Menu as MenuIcon, ShoppingCart, Truck, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { API } from "@/utils/constants";
import { CRMImportDialog } from "@/views/CRMImportDialog";
import { CRMCombobox } from "@/views/CRMCombobox";
import { CRMDashboard, isStale } from "@/views/CRMDashboard";
import { CRMInvoices } from "@/views/CRMInvoices";
import { CRMDocuments } from "@/views/CRMDocuments";
import { CRMExpenses } from "@/views/CRMExpenses";
import { CRMProducts } from "@/views/CRMProducts";

const DEAL_ACT_ICONS = { note: StickyNote, call: PhoneCall, meeting: CalendarClock, email: Mail, task: Check };

const CRM_MENU_ITEMS = [
  ["quotes", "Quotes", FileText],
  ["sales_orders", "Sales Orders", ShoppingCart],
  ["purchase_orders", "Purchase Orders", Truck],
  ["invoices", "Invoices", Receipt],
  ["products", "Products", Package],
  ["expenses", "Expenses", DollarSign],
];

const CRM_PRIMARY_ITEMS = [
  ["dashboard", "Dashboard", LayoutDashboard],
  ["pipeline", "Pipeline", Handshake],
  ["contacts", "Contacts", Users],
  ["companies", "Companies", Building2],
];

const CRM_ALL_ITEMS = [...CRM_PRIMARY_ITEMS, ...CRM_MENU_ITEMS];

const DealActivityLog = ({ dealId, onTouched }) => {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ type: "note", text: "" });
  const refresh = useCallback(async () => {
    try { const r = await axios.get(`${API}/crm/activities`, { params: { deal_id: dealId } }); setItems(r.data); } catch { setItems([]); }
  }, [dealId]);
  useEffect(() => { refresh(); }, [refresh]);

  const add = async () => {
    if (!form.text.trim()) return;
    try {
      await axios.post(`${API}/crm/activities`, { ...form, deal_id: dealId });
      setForm({ type: "note", text: "" });
      refresh(); onTouched?.();
    } catch { toast.error("Failed to log activity"); }
  };

  return (
    <div className="border-t pt-4 mt-4" data-testid="deal-activity-log">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Activity log</h4>
      <div className="flex gap-2 mb-3">
        <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
          <SelectTrigger className="w-24 shrink-0 h-9 text-xs" data-testid="deal-activity-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="note">Note</SelectItem><SelectItem value="call">Call</SelectItem>
            <SelectItem value="meeting">Meeting</SelectItem><SelectItem value="email">Email</SelectItem>
            <SelectItem value="task">Follow-up</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Log a call, note, meeting…" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid="deal-activity-input" />
        <Button type="button" size="icon" className="shrink-0" onClick={add} data-testid="deal-activity-add-btn"><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {items === null && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mx-auto my-2" />}
        {items?.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">No activity yet — logging one marks the deal as active.</p>}
        {items?.map((a) => {
          const Icon = DEAL_ACT_ICONS[a.type] || StickyNote;
          return (
            <div key={a.id} className="flex items-start gap-2.5 bg-muted/40 rounded-lg p-2.5" data-testid={`deal-activity-${a.id}`}>
              <Icon className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs">{a.text}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{a.type} · {new Date(a.created_at).toLocaleString()}</p>
              </div>
              <button type="button" onClick={async () => { await axios.delete(`${API}/crm/activities/${a.id}`); setItems(items.filter((x) => x.id !== a.id)); }} className="p-1 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-500"><X className="h-3 w-3" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const STAGES = [
  { key: "lead", label: "Lead", color: "bg-slate-400" },
  { key: "contacted", label: "Contacted", color: "bg-sky-500" },
  { key: "proposal", label: "Proposal", color: "bg-amber-500" },
  { key: "won", label: "Won", color: "bg-emerald-500" },
  { key: "lost", label: "Lost", color: "bg-red-400" },
];
const fmt = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v || 0);

const DealCard = ({ deal, contacts, members, onEdit }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: deal.id });
  const contact = contacts.find((c) => c.id === deal.contact_id);
  const owner = (members || []).find((m) => m.id === deal.owner_id);
  return (
    <div ref={setNodeRef} {...listeners} {...attributes}
      style={transform ? { transform: `translate(${transform.x}px, ${transform.y}px)`, zIndex: 50, position: "relative" } : undefined}
      onClick={() => !isDragging && onEdit(deal)}
      className={`bg-card border rounded-xl p-3 cursor-grab active:cursor-grabbing shadow-sm hover:border-primary/40 transition-colors ${isDragging ? "opacity-80 shadow-xl" : ""}`}
      data-testid={`deal-card-${deal.id}`}>
      <div className="text-sm font-medium mb-1.5 leading-snug">{deal.title}</div>
      {isStale(deal) && (
        <div className="flex items-center gap-1 text-[10px] font-medium text-amber-600 mb-1.5" data-testid={`stale-warning-${deal.id}`}>
          <AlertTriangle className="h-3 w-3 shrink-0" />No activity 7+ days
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-emerald-600">{fmt(deal.value)}</span>
        {contact && <span className="text-[10px] text-muted-foreground truncate max-w-[45%]">{contact.name}</span>}
      </div>
      {owner && (
        <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-dashed" data-testid={`deal-owner-${deal.id}`}>
          <span className="w-4 h-4 rounded-full bg-violet-500/15 text-violet-600 text-[8px] font-bold flex items-center justify-center shrink-0">{owner.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}</span>
          <span className="text-[10px] text-muted-foreground truncate">{owner.name}</span>
        </div>
      )}
    </div>
  );
};

const StageColumn = ({ stage, deals, contacts, members, onEdit }) => {
  const { setNodeRef, isOver } = useDroppable({ id: stage.key });
  const total = deals.reduce((s, d) => s + (d.value || 0), 0);
  return (
    <div ref={setNodeRef} className={`flex-1 min-w-[210px] rounded-2xl p-3 transition-colors ${isOver ? "bg-primary/10 ring-2 ring-primary/30" : "bg-muted/40"}`} data-testid={`stage-col-${stage.key}`}>
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${stage.color}`} />
          <span className="text-sm font-semibold">{stage.label}</span>
          <span className="text-xs text-muted-foreground">{deals.length}</span>
        </div>
        <span className="text-[10px] text-muted-foreground font-medium">{fmt(total)}</span>
      </div>
      <div className="space-y-2 min-h-[60px]">
        {deals.map((d) => <DealCard key={d.id} deal={d} contacts={contacts} members={members} onEdit={onEdit} />)}
      </div>
    </div>
  );
};


export default function CRMApp({ user, onBackToHub, onShowUpgrade, workspaces = [], activeWorkspaceId, onSwitchWorkspace }) {
  const [tab, setTab] = useState("pipeline");
  const [navOpen, setNavOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const exportCrm = async () => {
    try {
      const r = await axios.get(`${API}/crm/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "opero-crm-export.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("CRM exported to Excel");
    } catch { toast.error("Export failed"); }
  };
  const [deals, setDeals] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [members, setMembers] = useState([]);
  const [myDealsOnly, setMyDealsOnly] = useState(false);
  const [stats, setStats] = useState(null);
  const [dealModal, setDealModal] = useState(null);
  const [contactModal, setContactModal] = useState(null);
  const [companyModal, setCompanyModal] = useState(null);
  const [detailContact, setDetailContact] = useState(null);
  const [activities, setActivities] = useState([]);
  const [actForm, setActForm] = useState({ type: "note", text: "" });
  const [locked, setLocked] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = useCallback(async () => {
    try {
      const [d, c, co, s] = await Promise.all([
        axios.get(`${API}/crm/deals`), axios.get(`${API}/crm/contacts`),
        axios.get(`${API}/crm/companies`), axios.get(`${API}/crm/stats`),
      ]);
      setDeals(d.data); setContacts(c.data); setCompanies(co.data); setStats(s.data);
      try { const m = await axios.get(`${API}/members`); setMembers(m.data.filter((x) => x.type === "user")); } catch {}
    } catch (err) {
      if (err.response?.status === 403) { setLocked(true); setDeals([]); return; }
      toast.error("Failed to load CRM data"); setDeals([]);
    }
  }, []);

  useEffect(() => { setDeals(null); setLocked(false); setDetailContact(null); load(); }, [load, activeWorkspaceId]);

  const openActivities = async (contact) => {
    setDetailContact(contact);
    try { const r = await axios.get(`${API}/crm/activities`, { params: { contact_id: contact.id } }); setActivities(r.data); } catch {}
  };

  const addActivity = async (e) => {
    e.preventDefault();
    if (!actForm.text.trim()) return;
    try {
      await axios.post(`${API}/crm/activities`, { ...actForm, contact_id: detailContact.id });
      setActForm({ type: "note", text: "" });
      const r = await axios.get(`${API}/crm/activities`, { params: { contact_id: detailContact.id } });
      setActivities(r.data);
    } catch { toast.error("Failed to add activity"); }
  };

  const onDragEnd = async ({ active, over }) => {
    if (!over || !active) return;
    const deal = deals.find((d) => d.id === active.id);
    if (!deal || deal.stage === over.id) return;
    const prev = deals;
    setDeals(deals.map((d) => (d.id === active.id ? { ...d, stage: over.id } : d)));
    try { await axios.put(`${API}/crm/deals/${active.id}/stage`, { stage: over.id }); load(); }
    catch { setDeals(prev); toast.error("Failed to move deal"); }
  };

  const saveDeal = async (e) => {
    e.preventDefault();
    const payload = { title: dealModal.title, value: parseFloat(dealModal.value) || 0, stage: dealModal.stage || "lead", contact_id: dealModal.contact_id === "none" ? null : dealModal.contact_id, company_id: dealModal.company_id === "none" ? null : dealModal.company_id, notes: dealModal.notes || "", owner_id: !dealModal.owner_id || dealModal.owner_id === "none" ? null : dealModal.owner_id };
    try {
      if (dealModal.id) await axios.put(`${API}/crm/deals/${dealModal.id}`, payload);
      else await axios.post(`${API}/crm/deals`, payload);
      setDealModal(null); load(); toast.success("Deal saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save deal"); }
  };

  const saveContact = async (e) => {
    e.preventDefault();
    const payload = { name: contactModal.name, email: contactModal.email || "", phone: contactModal.phone || "", company_id: contactModal.company_id === "none" ? null : contactModal.company_id, notes: contactModal.notes || "" };
    try {
      if (contactModal.id) await axios.put(`${API}/crm/contacts/${contactModal.id}`, payload);
      else await axios.post(`${API}/crm/contacts`, payload);
      setContactModal(null); load(); toast.success("Contact saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save contact"); }
  };

  const saveCompany = async (e) => {
    e.preventDefault();
    try {
      if (companyModal.id) await axios.put(`${API}/crm/companies/${companyModal.id}`, { name: companyModal.name, industry: companyModal.industry || "", website: companyModal.website || "" });
      else await axios.post(`${API}/crm/companies`, { name: companyModal.name, industry: companyModal.industry || "", website: companyModal.website || "" });
      setCompanyModal(null); load(); toast.success("Company saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save company"); }
  };

  const quickCreateContact = async (name) => {
    try { const r = await axios.post(`${API}/crm/contacts`, { name }); setContacts([r.data, ...contacts]); toast.success(`Contact "${name}" added`); return r.data.id; }
    catch { toast.error("Failed to add contact"); return null; }
  };

  const quickCreateCompany = async (name) => {
    try { const r = await axios.post(`${API}/crm/companies`, { name }); setCompanies([r.data, ...companies]); toast.success(`Company "${name}" added`); return r.data.id; }
    catch { toast.error("Failed to add company"); return null; }
  };

  const createTaskFromDeal = async () => {
    try {
      const r = await axios.post(`${API}/crm/deals/${dealModal.id}/create-task`);
      toast.success(`Task created in "${r.data.column}" on your Project board`);
      setDealModal(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create task"); }
  };

  const createInvoiceFromDeal = async () => {
    try {
      const r = await axios.post(`${API}/crm/deals/${dealModal.id}/create-invoice`);
      toast.success(`Invoice ${r.data.number} created`);
      setDealModal(null); setTab("invoices");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create invoice"); }
  };

  const createQuoteFromDeal = async () => {
    try {
      const r = await axios.post(`${API}/crm/deals/${dealModal.id}/create-quote`);
      toast.success(`Quote ${r.data.number} created`);
      setDealModal(null); setTab("quotes");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create quote"); }
  };

  const del = async (kind, id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await axios.delete(`${API}/crm/${kind}/${id}`); load(); if (detailContact?.id === id) setDetailContact(null); }
    catch { toast.error("Failed to delete"); }
  };

  const isPro = stats?.is_pro;

  if (locked) return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" data-testid="crm-locked">
      <div className="w-14 h-14 rounded-2xl bg-violet-500/10 flex items-center justify-center mb-4"><Crown className="h-7 w-7 text-violet-500" /></div>
      <h2 className="text-xl font-bold mb-2">CRM is part of the Business Bundle</h2>
      <p className="text-sm text-muted-foreground max-w-sm mb-5">Unlock contacts, companies and a drag-and-drop deals pipeline — plus everything in Pro — for $12.99/month.</p>
      <div className="flex gap-3">
        <Button variant="outline" onClick={onBackToHub} data-testid="crm-locked-back"><ArrowLeft className="h-4 w-4 mr-1.5" />Back</Button>
        <Button onClick={onShowUpgrade} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="crm-locked-upgrade"><Crown className="h-4 w-4 mr-1.5" />Get the Bundle</Button>
      </div>
    </div>
  );

  if (deals === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";
  const ACT_ICONS = { note: StickyNote, call: PhoneCall, meeting: CalendarClock, email: Mail, task: Check };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="crm-app">
      {/* PM-style left icon rail (collapsed state) */}
      {!navOpen && (
        <div className="sidebar-rail" data-testid="crm-sidebar-rail">
          <button onClick={() => setNavOpen(true)} className="rail-btn" title="Expand menu" data-testid="crm-rail-expand-btn"><MenuIcon className="h-5 w-5" /></button>
          <div className="w-8 h-px bg-border my-1" />
          {CRM_ALL_ITEMS.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} className={`rail-btn ${tab === k ? "active" : ""}`} title={label} data-testid={`crm-rail-${k}`}>
              <Icon className="h-5 w-5" />
            </button>
          ))}
        </div>
      )}

      <div className="pl-14 sm:pl-16">
      <header className="border-b bg-card sticky top-0 z-20">
        <div className="px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={onBackToHub} data-testid="crm-back-to-hub"><ArrowLeft className="h-4 w-4 mr-1.5" />Hub</Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-violet-500/15 flex items-center justify-center"><Handshake className="h-4 w-4 text-violet-500" /></div>
            <h1 className="font-heading font-bold text-lg">CRM</h1>
          </div>
          <div className="flex items-center gap-2 ml-2 sm:ml-4 text-sm font-medium text-muted-foreground" data-testid="crm-active-section">
            <span className="text-border">/</span>
            {(() => { const [, label, Icon] = CRM_ALL_ITEMS.find(([k]) => k === tab) || []; return Icon ? <><Icon className="h-4 w-4 text-primary" /><span className="text-foreground">{label}</span></> : null; })()}
          </div>
          <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setImportOpen(true)} data-testid="crm-import-btn">
              <Upload className="h-3.5 w-3.5 mr-1.5" />Import
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={exportCrm} data-testid="crm-export-btn">
              <Download className="h-3.5 w-3.5 mr-1.5" />Export
            </Button>
            {workspaces.length > 1 && onSwitchWorkspace && (
              <Select value={activeWorkspaceId || ""} onValueChange={onSwitchWorkspace}>
                <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="crm-workspace-select">
                  <SelectValue placeholder="Workspace" />
                </SelectTrigger>
                <SelectContent>
                  {workspaces.map((w) => (
                    <SelectItem key={w.id} value={w.id} data-testid={`crm-workspace-option-${w.id}`}>{w.name}{w.type === "team" ? " (Team)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {stats && (
              <>
                <span className="hidden md:flex items-center gap-1" data-testid="crm-pipeline-value"><DollarSign className="h-3.5 w-3.5 text-emerald-500" />Pipeline: <b className="text-foreground">{fmt(stats.pipeline_value)}</b></span>
                <span className="hidden md:flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5 text-primary" />Won: <b className="text-foreground">{fmt(stats.won_value)}</b></span>
              </>
            )}
          </div>
        </div>
      </header>

      {/* PM-style slide-out menu */}
      {navOpen && <div className="sidebar-backdrop" onClick={() => setNavOpen(false)} data-testid="crm-sidebar-backdrop" />}
      <aside className={`sidebar ${navOpen ? "sidebar-open" : "sidebar-collapsed"}`} data-testid="crm-sidebar">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-violet-500/15 flex items-center justify-center"><Handshake className="h-4 w-4 text-violet-500" /></div>
              <h1 className="font-heading text-2xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">CRM</h1>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setNavOpen(false)} className="h-8 w-8" data-testid="crm-sidebar-close-btn"><X className="h-4 w-4" /></Button>
          </div>
          {stats && (
            <div className="grid grid-cols-2 gap-2 mb-6">
              <div className="stat-card rounded-lg p-3"><div className="text-lg font-bold text-primary">{fmt(stats.pipeline_value)}</div><div className="text-xs text-muted-foreground">Pipeline</div></div>
              <div className="stat-card rounded-lg p-3"><div className="text-lg font-bold text-emerald-600">{fmt(stats.won_value)}</div><div className="text-xs text-muted-foreground">Won</div></div>
            </div>
          )}
          <nav className="space-y-1">
            {CRM_PRIMARY_ITEMS.map(([k, label, Icon]) => (
              <button key={k} onClick={() => { setTab(k); setNavOpen(false); }} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === k ? "bg-gradient-to-r from-primary to-purple-600 text-white shadow-md" : "hover:bg-muted"}`} data-testid={`crm-side-${k}`}>
                <Icon className="h-4 w-4" />{label}
              </button>
            ))}
            <div className="pt-4 pb-1 px-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sales documents</div>
            {CRM_MENU_ITEMS.slice(0, 4).map(([k, label, Icon]) => (
              <button key={k} onClick={() => { setTab(k); setNavOpen(false); }} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === k ? "bg-gradient-to-r from-primary to-purple-600 text-white shadow-md" : "hover:bg-muted"}`} data-testid={`crm-side-${k}`}>
                <Icon className="h-4 w-4" />{label}
              </button>
            ))}
            <div className="pt-4 pb-1 px-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Operations</div>
            {CRM_MENU_ITEMS.slice(4).map(([k, label, Icon]) => (
              <button key={k} onClick={() => { setTab(k); setNavOpen(false); }} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === k ? "bg-gradient-to-r from-primary to-purple-600 text-white shadow-md" : "hover:bg-muted"}`} data-testid={`crm-side-${k}`}>
                <Icon className="h-4 w-4" />{label}
              </button>
            ))}
          </nav>
          <div className="mt-6 pt-4 border-t space-y-1">
            <button onClick={() => { setNavOpen(false); setImportOpen(true); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-muted text-muted-foreground" data-testid="crm-side-import"><Upload className="h-4 w-4" />Import data</button>
            <button onClick={() => { setNavOpen(false); exportCrm(); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-muted text-muted-foreground" data-testid="crm-side-export"><Download className="h-4 w-4" />Export data</button>
          </div>
        </div>
      </aside>

      <main className="p-4 sm:p-6">
        {tab === "dashboard" && (
          <CRMDashboard key={activeWorkspaceId || "personal"} deals={deals} contacts={contacts} stats={stats} onOpenDeal={(d) => setDealModal({ ...d, contact_id: d.contact_id || "none", company_id: d.company_id || "none" })} />
        )}

        {tab === "pipeline" && (
          <>
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-3">
                <p className="text-sm text-muted-foreground">{(myDealsOnly ? deals.filter((d) => d.owner_id === user?.user_id) : deals).length} deal{(myDealsOnly ? deals.filter((d) => d.owner_id === user?.user_id) : deals).length === 1 ? "" : "s"}</p>
                {members.length > 0 && (
                  <button onClick={() => setMyDealsOnly(!myDealsOnly)} className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${myDealsOnly ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground hover:bg-muted"}`} data-testid="my-deals-toggle">My deals</button>
                )}
              </div>
              <Button size="sm" onClick={() => setDealModal({ title: "", value: "", stage: "lead", contact_id: "none", company_id: "none", owner_id: "none" })} data-testid="add-deal-btn"><Plus className="h-4 w-4 mr-1" />Deal</Button>
            </div>
            <DndContext sensors={sensors} onDragEnd={onDragEnd}>
              <div className="flex gap-3 overflow-x-auto pb-4">
                {STAGES.map((s) => <StageColumn key={s.key} stage={s} deals={(myDealsOnly ? deals.filter((d) => d.owner_id === user?.user_id) : deals).filter((d) => d.stage === s.key)} contacts={contacts} members={members} onEdit={(d) => setDealModal({ ...d, contact_id: d.contact_id || "none", company_id: d.company_id || "none", owner_id: d.owner_id || "none" })} />)}
              </div>
            </DndContext>
          </>
        )}

        {tab === "contacts" && (
          <>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-muted-foreground">{contacts.length} contact{contacts.length === 1 ? "" : "s"}</p>
              <Button size="sm" onClick={() => setContactModal({ name: "", email: "", phone: "", company_id: "none", notes: "" })} data-testid="add-contact-btn"><Plus className="h-4 w-4 mr-1" />Contact</Button>
            </div>
            {contacts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-16">No contacts yet — add your first one.</p>
            ) : (
              <div className="border rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50"><tr><th className="text-left p-3 font-medium">Name</th><th className="text-left p-3 font-medium hidden sm:table-cell">Email</th><th className="text-left p-3 font-medium hidden md:table-cell">Phone</th><th className="text-left p-3 font-medium hidden md:table-cell">Company</th><th className="p-3 w-20"></th></tr></thead>
                  <tbody>
                    {contacts.map((c) => (
                      <tr key={c.id} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => openActivities(c)} data-testid={`contact-row-${c.id}`}>
                        <td className="p-3 font-medium">{c.name}</td>
                        <td className="p-3 text-muted-foreground hidden sm:table-cell">{c.email || "—"}</td>
                        <td className="p-3 text-muted-foreground hidden md:table-cell">{c.phone || "—"}</td>
                        <td className="p-3 text-muted-foreground hidden md:table-cell">{companyName(c.company_id)}</td>
                        <td className="p-3">
                          <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => setContactModal({ ...c, company_id: c.company_id || "none" })} className="p-1.5 rounded hover:bg-muted text-muted-foreground" data-testid={`edit-contact-${c.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                            <button onClick={() => del("contacts", c.id)} className="p-1.5 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-500" data-testid={`delete-contact-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === "companies" && (
          <>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-muted-foreground">{companies.length} compan{companies.length === 1 ? "y" : "ies"}</p>
              <Button size="sm" onClick={() => setCompanyModal({ name: "", industry: "", website: "" })} data-testid="add-company-btn"><Plus className="h-4 w-4 mr-1" />Company</Button>
            </div>
            {companies.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-16">No companies yet.</p>
            ) : (
              <div className="border rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50"><tr><th className="text-left p-3 font-medium">Name</th><th className="text-left p-3 font-medium hidden sm:table-cell">Industry</th><th className="text-left p-3 font-medium hidden md:table-cell">Website</th><th className="p-3 w-20"></th></tr></thead>
                  <tbody>
                    {companies.map((c) => (
                      <tr key={c.id} className="border-t hover:bg-muted/30" data-testid={`company-row-${c.id}`}>
                        <td className="p-3 font-medium">{c.name}</td>
                        <td className="p-3 text-muted-foreground hidden sm:table-cell">{c.industry || "—"}</td>
                        <td className="p-3 text-muted-foreground hidden md:table-cell">{c.website || "—"}</td>
                        <td className="p-3">
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => setCompanyModal(c)} className="p-1.5 rounded hover:bg-muted text-muted-foreground" data-testid={`edit-company-${c.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                            <button onClick={() => del("companies", c.id)} className="p-1.5 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-500" data-testid={`delete-company-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        {tab === "quotes" && <CRMDocuments docType="quote" refreshKey={activeWorkspaceId || "personal"} />}
        {tab === "sales_orders" && <CRMDocuments docType="sales_order" refreshKey={activeWorkspaceId || "personal"} />}
        {tab === "purchase_orders" && <CRMDocuments docType="purchase_order" refreshKey={activeWorkspaceId || "personal"} />}
        {tab === "invoices" && <CRMInvoices refreshKey={activeWorkspaceId || "personal"} />}
        {tab === "expenses" && <CRMExpenses refreshKey={activeWorkspaceId || "personal"} />}
        {tab === "products" && <CRMProducts refreshKey={activeWorkspaceId || "personal"} />}
      </main>

      {/* Deal drawer */}
      <Sheet open={!!dealModal} onOpenChange={(v) => !v && setDealModal(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="deal-drawer">
          <SheetHeader><SheetTitle>{dealModal?.id ? "Edit Deal" : "New Deal"}</SheetTitle></SheetHeader>
          {dealModal && (
            <form onSubmit={saveDeal} className="space-y-4 mt-4">
              {dealModal.id && isStale(dealModal) && (
                <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3" data-testid="deal-drawer-stale-banner">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>No activity on this deal for over 7 days — consider following up.</span>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Title</label>
                <Input required placeholder="Deal title" value={dealModal.title} onChange={(e) => setDealModal({ ...dealModal, title: e.target.value })} data-testid="deal-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Value ($)</label>
                  <Input type="number" min="0" step="0.01" placeholder="0" value={dealModal.value} onChange={(e) => setDealModal({ ...dealModal, value: e.target.value })} data-testid="deal-value-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Stage</label>
                  <Select value={dealModal.stage} onValueChange={(v) => setDealModal({ ...dealModal, stage: v })}>
                    <SelectTrigger data-testid="deal-stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Contact</label>
                <CRMCombobox items={contacts} value={dealModal.contact_id} onChange={(v) => setDealModal({ ...dealModal, contact_id: v })} placeholder="Type a contact name…" onCreate={quickCreateContact} testId="deal-contact-combobox" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Company</label>
                <CRMCombobox items={companies} value={dealModal.company_id} onChange={(v) => setDealModal({ ...dealModal, company_id: v })} placeholder="Type a company name…" onCreate={quickCreateCompany} testId="deal-company-combobox" />
              </div>
              {members.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Deal owner</label>
                  <Select value={dealModal.owner_id || "none"} onValueChange={(v) => setDealModal({ ...dealModal, owner_id: v })}>
                    <SelectTrigger data-testid="deal-owner-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.id === user?.user_id ? " (me)" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Notes</label>
                <Textarea rows={3} placeholder="Notes" value={dealModal.notes || ""} onChange={(e) => setDealModal({ ...dealModal, notes: e.target.value })} />
              </div>
              {dealModal.id && (
                dealModal.task_id ? (
                  <p className="text-xs text-emerald-600 flex items-center gap-1.5" data-testid="deal-task-linked"><Check className="h-3.5 w-3.5" />Task created on your Project board</p>
                ) : (
                  <Button type="button" variant="outline" size="sm" className="w-full" onClick={createTaskFromDeal} data-testid="create-task-from-deal-btn">
                    <Kanban className="h-4 w-4 mr-1.5" />Create task on Project board
                  </Button>
                )
              )}
              {dealModal.id && (
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={createInvoiceFromDeal} data-testid="create-invoice-from-deal-btn">
                    <FileText className="h-4 w-4 mr-1.5" />Create invoice
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={createQuoteFromDeal} data-testid="create-quote-from-deal-btn">
                    <FileText className="h-4 w-4 mr-1.5" />Create quote
                  </Button>
                </div>
              )}
              {dealModal.id && (dealModal.updated_at || dealModal.created_at) && (
                <p className="text-[10px] text-muted-foreground" data-testid="deal-last-activity">Last activity: {new Date(dealModal.updated_at || dealModal.created_at).toLocaleString()}</p>
              )}
              <div className="flex justify-between pt-1">
                {dealModal.id && <Button type="button" variant="ghost" className="text-red-500" onClick={() => { if (window.confirm("Delete this deal?")) { const id = dealModal.id; setDealModal(null); axios.delete(`${API}/crm/deals/${id}`).then(load).catch(() => toast.error("Failed to delete")); } }} data-testid="delete-deal-btn"><Trash2 className="h-4 w-4" /></Button>}
                <Button type="submit" className="ml-auto" data-testid="save-deal-btn">Save Deal</Button>
              </div>
            </form>
          )}
          {dealModal?.id && <DealActivityLog dealId={dealModal.id} onTouched={load} />}
        </SheetContent>
      </Sheet>

      {/* Contact modal */}
      <Dialog open={!!contactModal} onOpenChange={(v) => !v && setContactModal(null)}>
        <DialogContent className="max-w-md" data-testid="contact-modal">
          <DialogHeader><DialogTitle>{contactModal?.id ? "Edit Contact" : "New Contact"}</DialogTitle></DialogHeader>
          {contactModal && (
            <form onSubmit={saveContact} className="space-y-3">
              <Input required placeholder="Full name" value={contactModal.name} onChange={(e) => setContactModal({ ...contactModal, name: e.target.value })} data-testid="contact-name-input" />
              <div className="grid grid-cols-2 gap-3">
                <Input type="email" placeholder="Email" value={contactModal.email} onChange={(e) => setContactModal({ ...contactModal, email: e.target.value })} data-testid="contact-email-input" />
                <Input placeholder="Phone" value={contactModal.phone} onChange={(e) => setContactModal({ ...contactModal, phone: e.target.value })} data-testid="contact-phone-input" />
              </div>
              <CRMCombobox items={companies} value={contactModal.company_id} onChange={(v) => setContactModal({ ...contactModal, company_id: v })} placeholder="Type a company name…" onCreate={quickCreateCompany} testId="contact-company-combobox" />
              <Textarea rows={2} placeholder="Notes" value={contactModal.notes || ""} onChange={(e) => setContactModal({ ...contactModal, notes: e.target.value })} />
              <Button type="submit" className="w-full" data-testid="save-contact-btn">Save Contact</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Company modal */}
      <Dialog open={!!companyModal} onOpenChange={(v) => !v && setCompanyModal(null)}>
        <DialogContent className="max-w-md" data-testid="company-modal">
          <DialogHeader><DialogTitle>{companyModal?.id ? "Edit Company" : "New Company"}</DialogTitle></DialogHeader>
          {companyModal && (
            <form onSubmit={saveCompany} className="space-y-3">
              <Input required placeholder="Company name" value={companyModal.name} onChange={(e) => setCompanyModal({ ...companyModal, name: e.target.value })} data-testid="company-name-input" />
              <Input placeholder="Industry" value={companyModal.industry} onChange={(e) => setCompanyModal({ ...companyModal, industry: e.target.value })} data-testid="company-industry-input" />
              <Input placeholder="Website" value={companyModal.website} onChange={(e) => setCompanyModal({ ...companyModal, website: e.target.value })} data-testid="company-website-input" />
              <Button type="submit" className="w-full" data-testid="save-company-btn">Save Company</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Contact detail / activities */}
      <Dialog open={!!detailContact} onOpenChange={(v) => !v && setDetailContact(null)}>
        <DialogContent className="max-w-lg" data-testid="contact-detail-modal">
          {detailContact && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Users className="h-4 w-4 text-primary" />{detailContact.name}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-1">
                {detailContact.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{detailContact.email}</span>}
                {detailContact.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{detailContact.phone}</span>}
                {detailContact.company_id && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{companyName(detailContact.company_id)}</span>}
              </div>
              {detailContact.notes && <p className="text-xs bg-muted rounded-lg p-2.5">{detailContact.notes}</p>}
              <form onSubmit={addActivity} className="flex gap-2">
                <Select value={actForm.type} onValueChange={(v) => setActForm({ ...actForm, type: v })}>
                  <SelectTrigger className="w-28 shrink-0" data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Note</SelectItem><SelectItem value="call">Call</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem><SelectItem value="email">Email</SelectItem>
                    <SelectItem value="task">Follow-up</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Log an activity…" value={actForm.text} onChange={(e) => setActForm({ ...actForm, text: e.target.value })} data-testid="activity-text-input" />
                <Button type="submit" size="icon" className="shrink-0" data-testid="add-activity-btn"><Plus className="h-4 w-4" /></Button>
              </form>
              <div className="max-h-56 overflow-y-auto space-y-2" data-testid="activities-list">
                {activities.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No activity yet.</p>}
                {activities.map((a) => {
                  const Icon = ACT_ICONS[a.type] || StickyNote;
                  return (
                    <div key={a.id} className="flex items-start gap-2.5 bg-muted/40 rounded-lg p-2.5" data-testid={`activity-item-${a.id}`}>
                      <Icon className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs">{a.text}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{a.type} · {new Date(a.created_at).toLocaleString()}</p>
                      </div>
                      <button onClick={async () => { await axios.delete(`${API}/crm/activities/${a.id}`); setActivities(activities.filter((x) => x.id !== a.id)); }} className="p-1 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-500"><X className="h-3 w-3" /></button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <CRMImportDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />
      </div>
    </div>
  );
}
